import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  Avatar,
  Rating,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Tab,
  Tabs,
  Badge,
  Snackbar,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Extension as ExtensionIcon,
  Download as DownloadIcon,
  Verified as VerifiedIcon,
  Dashboard as DashboardIcon,
  Rule as RuleIcon,
  AccountTree as WorkflowIcon,
  Sync as IntegrationIcon,
  Build as UtilityIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { appService } from '../services/appService';
import { App, AppFilters, AppCategory, InstalledApp } from '../types/apps';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`marketplace-tabpanel-${index}`}
      aria-labelledby={`marketplace-tab-${index}`}
      {...other}
    >
      {value === index && children}
    </div>
  );
}

const getCategoryIcon = (category: AppCategory) => {
  switch (category) {
    case 'foundational':
      return <DashboardIcon />;
    case 'loan-processing':
      return <RuleIcon />;
    case 'workflow':
      return <WorkflowIcon />;
    case 'integration':
      return <IntegrationIcon />;
    case 'utility':
      return <UtilityIcon />;
    default:
      return <ExtensionIcon />;
  }
};

const categories: { value: AppCategory; label: string }[] = [
  { value: 'foundational', label: 'Foundational' },
  { value: 'utility', label: 'Utility' },
  { value: 'loan-processing', label: 'Loan Processing' },
  { value: 'integration', label: 'Integration' },
  { value: 'workflow', label: 'Workflow' },
];

export default function AppMarketplacePage() {
  const queryClient = useQueryClient();
  
  const [tabValue, setTabValue] = useState(0);
  const [filters, setFilters] = useState<AppFilters>({
    page: 1,
    limit: 12,
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedApp, setSelectedApp] = useState<App | null>(null);
  const [installDialogOpen, setInstallDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const showSnackbar = (message: string, severity: 'success' | 'error' = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  // Fetch marketplace apps
  const {
    data: marketplaceData,
    isLoading: isLoadingMarketplace,
    error: marketplaceError,
  } = useQuery({
    queryKey: ['marketplace-apps', filters],
    queryFn: () => appService.getMarketplaceApps(filters),
  });

  // Fetch installed apps
  const {
    data: installedApps,
    isLoading: isLoadingInstalled,
  } = useQuery({
    queryKey: ['installed-apps'],
    queryFn: () => appService.getInstalledApps(),
  });

  // Install app mutation
  const installMutation = useMutation({
    mutationFn: ({ appId, version }: { appId: string; version?: string }) =>
      appService.installApp(appId, version),
    onSuccess: (result) => {
      if (result.success) {
        showSnackbar('App installed successfully!', 'success');
        queryClient.invalidateQueries({ queryKey: ['installed-apps'] });
        setInstallDialogOpen(false);
      } else {
        showSnackbar(result.error || 'Failed to install app', 'error');
      }
    },
    onError: (error) => {
      showSnackbar('Failed to install app', 'error');
      console.error('Install error:', error);
    },
  });

  // Uninstall app mutation
  const uninstallMutation = useMutation({
    mutationFn: (appId: string) => appService.uninstallApp(appId),
    onSuccess: (result) => {
      if (result.success) {
        showSnackbar('App uninstalled successfully!', 'success');
        queryClient.invalidateQueries({ queryKey: ['installed-apps'] });
      } else {
        showSnackbar(result.error || 'Failed to uninstall app', 'error');
      }
    },
    onError: (error) => {
      showSnackbar('Failed to uninstall app', 'error');
      console.error('Uninstall error:', error);
    },
  });

  const handleSearch = () => {
    setFilters(prev => ({
      ...prev,
      search: searchQuery,
      page: 1,
    }));
  };

  const handleFilterChange = (key: keyof AppFilters, value: AppFilters[keyof AppFilters]) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1,
    }));
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleInstallApp = (app: App) => {
    setSelectedApp(app);
    setInstallDialogOpen(true);
  };

  const confirmInstallApp = () => {
    if (selectedApp) {
      installMutation.mutate({ appId: selectedApp.id });
    }
  };

  const handleUninstallApp = (appId: string) => {
    uninstallMutation.mutate(appId);
  };

  const isAppInstalled = (appId: string): InstalledApp | undefined => {
    return installedApps?.find(p => p.appId === appId);
  };

  const formatPrice = (app: App) => {
    if (app.pricing.model === 'free') {
      return 'Free';
    }
    const currency = app.pricing.currency || 'USD';
    const price = app.pricing.price || 0;
    const symbol = currency === 'USD' ? '$' : currency === 'EUR' ? '€' : '£';
    
    if (app.pricing.model === 'subscription') {
      const cycle = app.pricing.billingCycle === 'yearly' ? '/year' : '/month';
      return `${symbol}${price}${cycle}`;
    }
    
    return `${symbol}${price}`;
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          App Marketplace
        </Typography>
        <Typography variant="body1" color="text.secondary" gutterBottom>
          Discover and install apps to extend your iBusiness platform
        </Typography>
      </Box>

      <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)} sx={{ mb: 3 }}>
        <Tab label="Marketplace" />
        <Tab 
          label={
            <Badge badgeContent={installedApps?.length || 0} color="primary">
              Installed
            </Badge>
          } 
        />
      </Tabs>

      <TabPanel value={tabValue} index={0}>
        {/* Search and Filters */}
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                placeholder="Search apps..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
            </Grid>
            <Grid size={{ xs: 6, md: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={filters.category || ''}
                  label="Category"
                  onChange={(e) => handleFilterChange('category', e.target.value || undefined)}
                >
                  <MenuItem value="">All Categories</MenuItem>
                  {categories.map((category) => (
                    <MenuItem key={category.value} value={category.value}>
                      {category.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 6, md: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Price</InputLabel>
                <Select
                  value={filters.maxPrice || ''}
                  label="Price"
                  onChange={(e) => handleFilterChange('maxPrice', e.target.value || undefined)}
                >
                  <MenuItem value="">Any Price</MenuItem>
                  <MenuItem value="0">Free</MenuItem>
                  <MenuItem value="10">Under $10</MenuItem>
                  <MenuItem value="50">Under $50</MenuItem>
                  <MenuItem value="100">Under $100</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Button
                variant="contained"
                onClick={handleSearch}
                startIcon={<SearchIcon />}
                sx={{ mr: 1 }}
              >
                Search
              </Button>
              <Button
                variant="outlined"
                startIcon={<FilterIcon />}
                onClick={() => setFilters({ page: 1, limit: 12 })}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </Box>

        {/* Loading State */}
        {isLoadingMarketplace && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        )}

        {/* Error State */}
        {marketplaceError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Failed to load marketplace apps. Please try again.
          </Alert>
        )}

        {/* App Grid */}
        {marketplaceData && (
          <>
            <Grid container spacing={3}>
              {marketplaceData.apps.map((app) => {
                const installed = isAppInstalled(app.id);
                return (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }} key={app.id}>
                    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                            {getCategoryIcon(app.category)}
                          </Avatar>
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="h6" component="h2" noWrap>
                              {app.name}
                              {app.author.name.includes('iBusiness') && (
                                <VerifiedIcon sx={{ ml: 1, fontSize: 16, color: 'primary.main' }} />
                              )}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              by {app.author.name}
                            </Typography>
                          </Box>
                        </Box>

                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 40 }}>
                          {app.description}
                        </Typography>

                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Chip
                            label={categories.find(c => c.value === app.category)?.label || app.category}
                            size="small"
                            sx={{ mr: 1 }}
                          />
                          <Chip
                            label={formatPrice(app)}
                            size="small"
                            color={app.pricing.model === 'free' ? 'success' : 'default'}
                          />
                        </Box>

                        {app.ratings && (
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Rating value={app.ratings.average} readOnly size="small" />
                            <Typography variant="caption" sx={{ ml: 1 }}>
                              ({app.ratings.count})
                            </Typography>
                          </Box>
                        )}

                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <DownloadIcon sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">
                            {app.downloads || 0} downloads
                          </Typography>
                        </Box>
                      </CardContent>

                      <CardActions>
                        {installed ? (
                          <Button
                            fullWidth
                            variant="outlined"
                            color="error"
                            onClick={() => handleUninstallApp(app.id)}
                            disabled={uninstallMutation.isPending}
                          >
                            {uninstallMutation.isPending ? 'Uninstalling...' : 'Uninstall'}
                          </Button>
                        ) : (
                          <Button
                            fullWidth
                            variant="contained"
                            onClick={() => handleInstallApp(app)}
                            disabled={installMutation.isPending}
                          >
                            {installMutation.isPending ? 'Installing...' : 'Install'}
                          </Button>
                        )}
                      </CardActions>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>

            {/* Pagination */}
            {marketplaceData.pagination.pages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <Pagination
                  count={marketplaceData.pagination.pages}
                  page={marketplaceData.pagination.page}
                  onChange={handlePageChange}
                  color="primary"
                />
              </Box>
            )}
          </>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        {/* Installed Apps */}
        {isLoadingInstalled && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        )}

        {installedApps && installedApps.length === 0 && (
          <Box textAlign="center" py={8}>
            <ExtensionIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              No apps installed
            </Typography>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              Browse the marketplace to find apps for your workflow
            </Typography>
            <Button
              variant="contained"
              onClick={() => setTabValue(0)}
              sx={{ mt: 2 }}
            >
              Browse Marketplace
            </Button>
          </Box>
        )}

        {installedApps && installedApps.length > 0 && (
          <Grid container spacing={3}>
            {installedApps.map((installation) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={installation.appId}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                        {installation.app ? getCategoryIcon(installation.app.category) : <ExtensionIcon />}
                      </Avatar>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="h6" component="h2">
                          {installation.app?.name || 'Unknown App'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          v{installation.version}
                        </Typography>
                      </Box>
                      <Chip
                        label={installation.isEnabled ? 'Enabled' : 'Disabled'}
                        color={installation.isEnabled ? 'success' : 'default'}
                        size="small"
                      />
                    </Box>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {installation.app?.description || 'No description available'}
                    </Typography>

                    <Typography variant="caption" color="text.secondary">
                      Installed: {new Date(installation.installedAt).toLocaleDateString()}
                    </Typography>
                  </CardContent>

                  <CardActions>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={() => handleUninstallApp(installation.appId)}
                      disabled={uninstallMutation.isPending}
                    >
                      Uninstall
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </TabPanel>

      {/* Install Confirmation Dialog */}
      <Dialog open={installDialogOpen} onClose={() => setInstallDialogOpen(false)}>
        <DialogTitle>
          Install App
          <IconButton
            aria-label="close"
            onClick={() => setInstallDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedApp && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {selectedApp.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                by {selectedApp.author.name}
              </Typography>
              <Typography variant="body1" gutterBottom>
                {selectedApp.description}
              </Typography>
              
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Price: {formatPrice(selectedApp)}
                </Typography>
                <Typography variant="subtitle2" gutterBottom>
                  Version: {selectedApp.version}
                </Typography>
              </Box>

              {selectedApp.manifest.permissions.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Required Permissions:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selectedApp.manifest.permissions.map((permission) => (
                      <Chip
                        key={permission}
                        label={permission}
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInstallDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={confirmInstallApp}
            disabled={installMutation.isPending}
          >
            {installMutation.isPending ? 'Installing...' : 'Install'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}